'use client';

/**
 * Suggested curators — two variants:
 *
 *  - `empty-state` (/following with no follows):
 *    follow → card removed. When all gone → "You're all set" nudge.
 *
 *  - `home-widget` (/):
 *    follow → card stays, button flips. Standard discovery-feed behaviour.
 */

import Link from 'next/link';
import { useEffect, useState } from 'react';
import { SuggestedUser } from '@/types';
import { api } from '@/lib/api';
import UserCardRow from '@/components/users/UserCardRow';

type Props = {
  variant: 'empty-state' | 'home-widget';
  limit?: number;
  heading?: string;
};

export default function SuggestedCuratorsBlock({ variant, limit = 10, heading }: Props) {
  const [users, setUsers] = useState<SuggestedUser[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.users.suggested(limit)
      .then(setUsers)
      .catch(() => setUsers([]))
      .finally(() => setLoading(false));
  }, [limit]);

  const handleFollowed = (username: string, nowFollowing: boolean) => {
    if (variant === 'empty-state' && nowFollowing) {
      setUsers((prev) => prev.filter((u) => u.username !== username));
    }
  };

  /* ── Skeleton ── */
  if (loading) {
    return (
      <div style={{ padding: '24px 0' }}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
          gap: 12,
        }}>
          {Array.from({ length: Math.min(4, limit) }).map((_, i) => (
            <div key={i} style={{
              height: 148,
              background: 'var(--bg-soft)',
              border: '1px solid var(--line)',
              borderRadius: 'var(--radius-md)',
              opacity: 0.5 + i * 0.12,
              animation: 'pulse 1.4s ease-in-out infinite',
            }} />
          ))}
        </div>
      </div>
    );
  }

  /* ── All followed (empty-state) or no suggestions ── */
  if (users.length === 0) {
    if (variant === 'empty-state') {
      return (
        <div style={{
          padding: '36px 24px',
          background: 'var(--bg-soft)',
          border: '1px solid var(--line)',
          borderRadius: 'var(--radius-md)',
          textAlign: 'center',
        }}>
          <span className="material-symbols-outlined" style={{ fontSize: 36, color: 'var(--accent)', marginBottom: 8, display: 'block' }}>
            celebration
          </span>
          <div style={{
            fontFamily: 'var(--font-serif)', fontSize: 20, fontWeight: 700,
            color: 'var(--ink)', marginBottom: 6, letterSpacing: '-0.02em',
          }}>
            You&rsquo;re all set!
          </div>
          <p style={{ fontSize: 13, color: 'var(--ink-3)', margin: '0 0 16px', lineHeight: 1.55 }}>
            Your <Link href="/following" className="no-underline" style={{ color: 'var(--accent)' }}>feed</Link>{' '}
            will fill as the curators you follow publish new prompts.
          </p>
          <Link href="/following" className="ps-btn ps-btn-primary ps-btn-sm no-underline">
            <span className="material-symbols-outlined ps-icon">dynamic_feed</span>
            Go to my feed
          </Link>
        </div>
      );
    }
    return null;
  }

  // When used inside another container (heading=''), hug the parent — no top margin.
  const sectionMarginTop =
    heading === '' ? 0 : (variant === 'home-widget' ? 48 : 24);

  return (
    <section style={{ marginTop: sectionMarginTop }}>
      {/* Section header */}
      {heading !== '' && (
        <div style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          marginBottom: 16,
          gap: 12,
        }}>
          <div>
            <div style={{
              fontFamily: 'var(--font-label)',
              fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase',
              color: 'var(--accent)', fontWeight: 700, marginBottom: 5,
            }}>
              {variant === 'empty-state' ? 'Start here' : 'Discover'}
            </div>
            <h2 style={{
              fontFamily: 'var(--font-serif)',
              fontSize: 'clamp(20px, 2.2vw, 26px)',
              fontWeight: 400,
              fontVariationSettings: '"opsz" 110, "SOFT" 50',
              letterSpacing: '-0.02em',
              color: 'var(--ink)',
              margin: 0,
              lineHeight: 1.1,
            }}>
              {heading ?? (
                variant === 'empty-state'
                  ? 'Curators people are loving'
                  : 'Curators to follow'
              )}
            </h2>
          </div>

          {variant === 'home-widget' && (
            <Link href="/following" className="no-underline" style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              fontSize: 12, color: 'var(--ink-3)',
              whiteSpace: 'nowrap',
              transition: 'color 0.15s',
            }}>
              See feed
              <span className="material-symbols-outlined" style={{ fontSize: 14 }}>arrow_forward</span>
            </Link>
          )}
        </div>
      )}

      {/* Card grid */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
        gap: 16,
        alignItems: 'stretch',
      }}>
        {users.map((u) => (
          <UserCardRow
            key={u.id}
            user={u}
            layout="card"
            onFollowed={handleFollowed}
          />
        ))}
      </div>
    </section>
  );
}
